import { applicationErrorMessage } from "../lib/applicationErrors.mjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

const DEST = {
  visa_request: { label: "visa support request", to: "/visa-support" },
  verification_case: { label: "verification case", to: "/verify" },
  consultation_booking: { label: "appointment", to: "/consultations" },
  course: { label: "course enrolment", to: "/academy" },
  cv_marketing_package: { label: "CV marketing campaign", to: "/cv-marketing" },
};

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling" });
  const [retryVersion, setRetryVersion] = useState(0);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", message: "No payment session was supplied." }); return; }
    let stop = false;
    let timer;
    let attempts = 0;
    setState({ status: "polling" });
    const poll = async () => {
      if (stop) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${encodeURIComponent(sessionId)}`);
        if (stop) return;
        if (data.payment_status === "paid") { setState({ status: "paid", data }); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState({ status: "failed", data }); return; }
      } catch (error) {
        if (stop) return;
        setState({ status: "error", message: error?.response?.status === 404
          ? "That payment session could not be found for your account."
          : "We could not check your payment right now. Retry confirmation or check your dashboard before making another payment." });
        return;
      }
      if (attempts >= 12) { setState({ status: "timeout" }); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; clearTimeout(timer); };
  }, [sessionId, retryVersion]);

  const dest = state.data ? DEST[state.data.item_type] : null;

  return (
    <div className="max-w-2xl mx-auto px-5 py-20 text-center" data-testid="payment-success-page">
      {state.status === "polling" && (
        <>
          <Clock className="w-10 h-10 mx-auto text-[#1d4ed8] animate-pulse" />
          <h1 className="mt-6 text-3xl font-semibold">Confirming your payment</h1>
          <p className="mt-3 text-sm text-slate-600">Please wait while Stripe confirms the transaction. Do not close this page.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600" />
          <h1 className="mt-6 text-3xl font-semibold" data-testid="payment-confirmed-heading">Payment confirmed</h1>
          <p className="mt-3 text-sm text-slate-700">{state.data.description} — {money(state.data.amount_pence)}</p>
          {state.data.warning ? (
            <p className="mt-3 text-sm text-amber-800" role="status" data-testid="payment-fulfilment-warning">
              {state.data.warning} Reference: {state.data.session_id}
            </p>
          ) : (
            <p className="mt-2 text-sm text-slate-600">Your payment has been processed. Check your {dest?.label || "order"} for its current status.</p>
          )}
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/dashboard" className="btn btn-primary" data-testid="success-dashboard-link">Go to dashboard</Link>
            {dest && <Link to={dest.to} className="btn btn-ghost">View {dest.label}</Link>}
          </div>
        </>
      )}
      {(state.status === "failed" || state.status === "error" || state.status === "timeout") && (
        <>
          <XCircle className="w-10 h-10 mx-auto text-red-600" />
          <h1 className="mt-6 text-3xl font-semibold" data-testid="payment-problem-heading">Payment not confirmed</h1>
          <p className="mt-3 text-sm text-slate-600">
            {state.message || "Payment confirmation has not arrived yet. Check your dashboard or retry confirmation before making another payment."}
          </p>
          <div className="mt-8 flex justify-center gap-3">
            {sessionId && state.status !== "failed" && <button className="btn btn-primary" onClick={() => setRetryVersion((version) => version + 1)}>Retry confirmation</button>}
            <Link to="/dashboard" className="btn btn-ghost">Return to dashboard</Link>
          </div>
        </>
      )}
      <p className="mt-10 text-xs text-slate-500">Payment confirmation is provided by the payment provider. Check your dashboard for the current service status.</p>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-2xl mx-auto px-5 py-20 text-center" data-testid="payment-cancel-page">
      <XCircle className="w-10 h-10 mx-auto text-amber-600" />
      <h1 className="mt-6 text-3xl font-semibold">Payment cancelled</h1>
      <p className="mt-3 text-sm text-slate-600">
        Checkout was closed. Check your dashboard for payment and service status before starting another payment.
      </p>
      <Link to="/dashboard" className="btn btn-primary mt-8" data-testid="cancel-dashboard-link">Return to dashboard</Link>
    </div>
  );
}

export function CvMarketing() {
  const [packages, setPackages] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pending, setPending] = useState("");
  const loadVersion = useRef(0);
  const actionPending = useRef(false);
  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true); setLoadError("");
    try {
      const { data } = await api.get("/payments/packages");
      if (version === loadVersion.current) setPackages(Array.isArray(data) ? data : []);
    } catch (error) {
      if (version === loadVersion.current) setLoadError(applicationErrorMessage(error, "Unable to load packages. Please try again."));
    } finally { if (version === loadVersion.current) setLoading(false); }
  }, []);
  const invalidateLoad = useCallback(() => { ++loadVersion.current; }, []);
  useEffect(() => { load(); return invalidateLoad; }, [load, invalidateLoad]);

  const buy = async (id) => {
    if (actionPending.current) return;
    actionPending.current = true; setPending(id); setMsg("");
    try {
      const { data } = await api.post("/payments/checkout", {
        item_type: "cv_marketing_package", item_id: id, origin_url: window.location.origin,
      });
      if (!data.checkout_url) throw new Error("Checkout could not be opened. Please try again.");
      window.location.href = data.checkout_url;
    } catch (error) { setMsg(applicationErrorMessage(error, "Unable to open checkout. Please sign in and try again.")); }
    finally { actionPending.current = false; setPending(""); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-10" data-testid="cv-marketing-page">
      <p className="overline">EuropEdu Sponsored Employer CV Marketing Package</p>
      <h1 className="mt-3 text-3xl sm:text-4xl font-semibold">Promote your profile to licensed employers</h1>
      <p className="mt-4 max-w-3xl text-sm text-slate-600" data-testid="cv-marketing-disclaimer">
        EuropEdu's CV Marketing Package is a candidate-profile promotion service. Purchase does not guarantee
        an employer response, interview, job offer, Certificate of Sponsorship, visa approval or employment
        placement. A sponsor licence does not mean that an organisation is currently recruiting or will
        sponsor a particular candidate.
      </p>
      {msg && <p role="status" data-testid="cv-marketing-message" className="mt-4 text-sm text-red-700">{msg}</p>}
      {loading && <p role="status" className="mt-6 text-sm">Loading packages…</p>}
      {loadError && <div className="mt-6"><p role="alert" className="text-sm text-red-700">{loadError}</p><button onClick={load} className="btn btn-ghost mt-3">Retry loading packages</button></div>}
      {!loading && !loadError && packages.length === 0 && <p className="mt-6 text-sm">No packages are available yet.</p>}
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {!loading && !loadError && packages.map((p) => (
          <article key={p.id} className="card p-6" data-testid={`package-${p.name}`}>
            <h2 className="text-xl font-semibold">{p.name}</h2>
            <p className="mt-2 text-2xl font-semibold text-[#1e3a8a]">{money(p.price_pence)}</p>
            <ul className="mt-4 text-sm space-y-1 text-slate-700">
              <li>{p.employer_count} licensed employers contacted</li>
              <li>{p.duration_weeks}-week campaign</li>
              <li>{p.follow_ups} follow-up round(s)</li>
              <li>{p.consultations} included consultation(s)</li>
              <li>{(p.included_cv_services || []).join(", ")}</li>
            </ul>
            <button data-testid={`buy-package-${p.name}`} disabled={!!pending} onClick={() => buy(p.id)} className="btn btn-primary mt-6 w-full justify-center">
              {pending === p.id ? "Opening checkout…" : <>Purchase {p.name}</>}
            </button>
          </article>
        ))}
      </div>
      <p className="mt-8 text-xs text-slate-500">
        After purchase you complete a candidate intake, review your CV and approve the marketing profile
        before any outreach begins. Every employer contacted, the reason for selection, the date sent,
        delivery status, follow-ups and responses are recorded on your campaign.
      </p>
    </div>
  );
}

